import { AreaChart, Area, Tooltip, ResponsiveContainer } from "recharts";
import {
  ChartGrid,
  ChartXAxis,
  ChartYAxis,
  CHART_TOOLTIP_STYLE,
} from "../lens/chart-primitives";
import { formatUsd, formatCompact } from "../../format";
import { cn } from "../../utils";

export interface FleetTrendPoint {
  date: string;
  /** null = no data that day — the area GAPS (recharts connectNulls=false),
   *  never plots 0 (which would read as perfect efficiency on a leaner axis). */
  value: number | null;
}

interface FleetTrendChartProps {
  points: FleetTrendPoint[];
  color: string;
  /** How to format the value on the axis + tooltip. */
  unit?: "count" | "usd";
  /** Tooltip label for the value. */
  valueLabel: string;
  height?: number;
  className?: string;
}

function shortDate(iso: string): string {
  const [, m, d] = iso.split("-");
  return m && d ? `${Number(m)}/${Number(d)}` : iso;
}

/**
 * Single-series trend area — one metric over time (e.g. tokens per landed task).
 * The efficiency spine of the Agents ▸ Analytics ▸ Quality view: is the fleet
 * getting leaner per landed unit of work? Days with null values gap the line so
 * "no data" never masquerades as "zero". Presentational only.
 */
export function FleetTrendChart({
  points,
  color,
  unit = "count",
  valueLabel,
  height = 200,
  className,
}: FleetTrendChartProps) {
  if (points.every((p) => p.value == null)) {
    return (
      <div
        className={cn(
          "flex items-center justify-center type-body text-muted-foreground",
          className,
        )}
        style={{ height }}
      >
        Not enough landed tasks in this range to chart.
      </div>
    );
  }
  const fmt = (v: number) => (unit === "usd" ? formatUsd(v) : formatCompact(v));
  return (
    <div className={className}>
      <ResponsiveContainer width="100%" height={height} minWidth={0}>
        <AreaChart data={points} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
          <ChartGrid />
          <ChartXAxis
            dataKey="date"
            tickFormatter={shortDate}
            interval="preserveStartEnd"
            minTickGap={42}
          />
          <ChartYAxis tickFormatter={(v: number) => fmt(v)} width={52} />
          <Tooltip
            contentStyle={CHART_TOOLTIP_STYLE}
            labelFormatter={(l) => String(l)}
            formatter={(val) => [
              fmt(typeof val === "number" ? val : 0),
              valueLabel,
            ]}
          />
          <Area
            type="monotone"
            dataKey="value"
            stroke={color}
            fill={color}
            fillOpacity={0.18}
            strokeWidth={1.5}
            connectNulls={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
